'use client'

import { useEffect, useMemo, useRef } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { UseFormReturn } from 'react-hook-form'
import { TagIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FormControl, FormItem, FormLabel } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { NumberInput } from '@/components/ui/number-input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useCategory } from '@/hooks'
import { useMetaStore } from '@/store'
import { CategoryPropertyType } from '@/types/category'
import { PropertyField } from '@/types/property'
import { FieldTypeEnum } from '@/enums'

export type PropertyFieldValue = {
  id: string
  value: string
}

export type PropertyFieldsData = {
  id: string
  fields: PropertyFieldValue[]
}

type PropertyFieldsProps = {
  categoryId: string
  form: UseFormReturn<any>
  fieldName: string // form field name for properties array
  initialData?: PropertyFieldsData[]
}

// Get localized field name
const getFieldName = (field: PropertyField, locale: string, defaultLocale?: string): string => {
  if (field.translations[locale]) {
    return field.translations[locale].name
  }
  if (defaultLocale && field.translations[defaultLocale]) {
    return field.translations[defaultLocale].name
  }
  const firstTranslation = Object.values(field.translations)[0]
  return firstTranslation?.name || field.id
}

// Get a localized property name
const getPropertyName = (property: CategoryPropertyType, locale: string, defaultLocale?: string): string => {
  if (property.property.translations[locale]) {
    return property.property.translations[locale].name
  }
  if (defaultLocale && property.property.translations[defaultLocale]) {
    return property.property.translations[defaultLocale].name
  }
  const firstTranslation = Object.values(property.property.translations)[0]
  return firstTranslation?.name || property.property.key
}

// Render field input based on type
type FieldInputProps = {
  field: PropertyField
  value: string
  onChange: (value: string) => void
  error?: boolean
  fieldLabel: string
  id?: string
}

const FieldInput = ({ field, value, onChange, error, fieldLabel, id }: FieldInputProps) => {
  switch (field.type) {
    case FieldTypeEnum.TEXT:
      return (
        <Textarea
          id={id}
          placeholder={fieldLabel}
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          aria-invalid={error ? 'true' : undefined}
        />
      )
    case FieldTypeEnum.INTEGER:
      return (
        <NumberInput
          id={id}
          placeholder={fieldLabel}
          value={value === '' ? undefined : Number(value)}
          onValueChange={v => onChange(v === undefined ? '' : String(v))}
          stepper={1}
          error={error}
        />
      )
    case FieldTypeEnum.FLOAT:
      return (
        <NumberInput
          id={id}
          placeholder={fieldLabel}
          value={value === '' ? undefined : Number(value)}
          onValueChange={v => onChange(v === undefined ? '' : String(v))}
          stepper={0.01}
          decimalScale={10}
          error={error}
        />
      )
    case FieldTypeEnum.STRING:
    default:
      return (
        <Input
          id={id}
          placeholder={fieldLabel}
          value={value}
          onChange={e => onChange(e.target.value)}
          aria-invalid={error ? 'true' : undefined}
        />
      )
  }
}

const PropertyFieldsSkeleton = () => (
  <Card>
    <CardHeader>
      <Skeleton className="h-6 w-48" />
      <Skeleton className="h-4 w-64" />
    </CardHeader>
    <CardContent className="space-y-6">
      <div className="space-y-4">
        <Skeleton className="h-5 w-32" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
)

const PropertyFieldsForm = ({ categoryId, form, fieldName, initialData }: PropertyFieldsProps) => {
  const t = useTranslations()
  const locale = useLocale()
  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale

  const { data: category, isLoading } = useCategory(categoryId)

  // Use refs to avoid infinite loops
  const formRef = useRef(form)
  const initialDataRef = useRef(initialData)
  const lastCategoryIdRef = useRef<string | null>(null)

  // Update refs when values change
  formRef.current = form
  initialDataRef.current = initialData

  // Sort properties by order
  const sortedProperties = useMemo(() => {
    if (!category?.properties) {
      return []
    }
    return [...category.properties].sort((a, b) => a.order - b.order)
  }, [category?.properties])

  // Initialize form values when the category changes
  useEffect(() => {
    if (!category?.properties || category.properties.length === 0) {
      formRef.current.setValue(fieldName, [])
      return
    }

    // Check if the form already has this category's properties
    const currentFormProperties = formRef.current.getValues(fieldName) || []
    const hasCurrentCategoryProperties =
      currentFormProperties.length > 0 &&
      currentFormProperties.some((p: PropertyFieldsData) => category.properties?.some(cp => cp.id === p.id))

    // If categoryId changed or form doesn't have properties, set them
    if (lastCategoryIdRef.current !== categoryId || !hasCurrentCategoryProperties) {
      lastCategoryIdRef.current = categoryId

      const propertiesData: PropertyFieldsData[] = category.properties.map(prop => {
        // Find existing data for this property
        const existingData = initialDataRef.current?.find(d => d.id === prop.id)

        return {
          id: prop.id,
          fields: prop.property.fields.map(field => {
            // Find existing value for this field
            const existingField = existingData?.fields.find(f => f.id === field.id)
            return {
              id: field.id,
              value: existingField?.value || ''
            }
          })
        }
      })

      formRef.current.setValue(fieldName, propertiesData)
    }
  }, [categoryId, category?.properties, fieldName])

  // Get form errors for a specific field by IDs
  const getFieldError = (propertyId: string, fieldId: string): boolean => {
    const errors = form.formState.errors[fieldName] as any
    if (!errors) {
      return false
    }
    const currentValues = form.getValues(fieldName) || []
    const propIndex = currentValues.findIndex((p: PropertyFieldsData) => p.id === propertyId)
    if (propIndex === -1) {
      return false
    }
    const fieldIndex = currentValues[propIndex]?.fields?.findIndex((f: PropertyFieldValue) => f.id === fieldId)
    if (fieldIndex === -1) {
      return false
    }
    return !!errors?.[propIndex]?.fields?.[fieldIndex]?.value
  }

  // Get an error message for a specific field by IDs
  const getFieldErrorMessage = (propertyId: string, fieldId: string): string | undefined => {
    const errors = form.formState.errors[fieldName] as any
    if (!errors) {
      return undefined
    }
    const currentValues = form.getValues(fieldName) || []
    const propIndex = currentValues.findIndex((p: PropertyFieldsData) => p.id === propertyId)
    if (propIndex === -1) {
      return undefined
    }
    const fieldIndex = currentValues[propIndex]?.fields?.findIndex((f: PropertyFieldValue) => f.id === fieldId)
    if (fieldIndex === -1) {
      return undefined
    }
    return errors?.[propIndex]?.fields?.[fieldIndex]?.value?.message
  }

  // Get field value by IDs
  const getFieldValue = (propertyId: string, fieldId: string): string => {
    const currentValues = form.watch(fieldName) || []
    const property = currentValues.find((p: PropertyFieldsData) => p.id === propertyId)
    if (!property) {
      return ''
    }
    const field = property.fields?.find((f: PropertyFieldValue) => f.id === fieldId)
    return field?.value || ''
  }

  // Update field value by IDs
  const updateFieldValue = (propertyId: string, fieldId: string, value: string) => {
    const currentValues = form.getValues(fieldName) || []
    const newValues = [...currentValues]
    const propIndex = newValues.findIndex((p: PropertyFieldsData) => p.id === propertyId)
    if (propIndex === -1) {
      return
    }
    const fieldIndex = newValues[propIndex]?.fields?.findIndex((f: PropertyFieldValue) => f.id === fieldId)
    if (fieldIndex === -1) {
      return
    }
    newValues[propIndex].fields[fieldIndex].value = value
    form.setValue(fieldName, newValues, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
  }

  if (!categoryId) {
    return null
  }

  if (isLoading) {
    return <PropertyFieldsSkeleton />
  }

  if (!category?.properties || category.properties.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TagIcon className="h-5 w-5" />
          {t('category_properties')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {sortedProperties.map(categoryProperty => {
          const propertyName = getPropertyName(categoryProperty, locale, defaultLocale)
          const sortedFields = [...categoryProperty.property.fields].sort((a, b) => a.order - b.order)

          return (
            <div key={categoryProperty.id} className="space-y-4">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold">{propertyName}</h4>
                <Badge variant="outline" className="text-xs">
                  {categoryProperty.property.key}
                </Badge>
              </div>
              <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                {sortedFields.map(field => {
                  const fieldLabel = getFieldName(field, locale, defaultLocale)
                  const hasError = getFieldError(categoryProperty.id, field.id)
                  const errorMessage = getFieldErrorMessage(categoryProperty.id, field.id)
                  const currentValue = getFieldValue(categoryProperty.id, field.id)
                  const inputId = `property-${categoryProperty.id}-field-${field.id}`

                  return (
                    <FormItem key={field.id}>
                      <FormLabel htmlFor={inputId} className="flex items-center gap-2">
                        {fieldLabel}
                        {field.required && <span className="text-destructive">*</span>}
                      </FormLabel>
                      <FormControl>
                        <FieldInput
                          field={field}
                          value={currentValue}
                          onChange={value => updateFieldValue(categoryProperty.id, field.id, value)}
                          error={hasError}
                          fieldLabel={fieldLabel}
                          id={inputId}
                        />
                      </FormControl>
                      {hasError && (
                        <p className="text-destructive text-sm font-medium">{errorMessage || t('not_blank')}</p>
                      )}
                    </FormItem>
                  )
                })}
              </div>
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

export default PropertyFieldsForm
